import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { logout } from '../../store/slices/authSlice';
import { tokenManager, securityUtils } from '../../services/api';
import { useTokenMonitor } from '../../hooks/useSecureApi';
import LoadingSpinner from './LoadingSpinner';

// Higher-order component for secure admin pages
const withSecureAuth = (WrappedComponent, requiredPermissions = []) => {
  return function SecureAdminPage(props) {
    const dispatch = useDispatch();
    const { isAuthenticated, admin, loading } = useSelector(state => state.auth);
    const { tokenStatus, checkTokenStatus, isTokenValid } = useTokenMonitor();

    useEffect(() => {
      // Check token status on component mount
      checkTokenStatus();
      
      // Set up token monitoring interval
      const interval = setInterval(() => {
        checkTokenStatus();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }, [checkTokenStatus]);

    // Show loading while checking authentication
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <LoadingSpinner text="Verifying authentication..." />
        </div>
      );
    }

    // Redirect to login if not authenticated
    if (!isAuthenticated || !isTokenValid) {
      return <Navigate to="/login" replace />;
    }

    // Check permissions if required
    if (requiredPermissions.length > 0 && admin) {
      // Debug logging to understand the admin object structure
      console.log('🔍 Debug - Admin object:', admin);
      console.log('🔍 Debug - Required permissions:', requiredPermissions);
      console.log('🔍 Debug - Admin permissions:', admin.permissions);
      console.log('🔍 Debug - Admin role:', admin.role);
      
      // For now, allow all authenticated admins access to all pages
      // TODO: Implement proper permission checking with AccessControl table
      const hasPermission = true; // Temporarily allow all authenticated users
      
      if (!hasPermission) {
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md mx-auto text-center">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <strong className="font-bold">Access Denied!</strong>
                <span className="block sm:inline"> You don't have permission to access this page.</span>
              </div>
              <button
                onClick={() => window.history.back()}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
              >
                Go Back
              </button>
            </div>
          </div>
        );
      }
    }

    // Show token expiration warning
    if (tokenStatus === 'expiring_soon') {
      console.warn('🔒 Token expiring soon - consider refreshing');
    }

    // Render the wrapped component
    return <WrappedComponent {...props} />;
  };
};

// Decorator for specific permission requirements
export const requirePermissions = (...permissions) => (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, permissions);
};

// Specific HOCs for common permission levels
export const requireAdminAccess = (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, ['admins', 'admin_management']);
};

export const requireTitheAccess = (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, ['tithe_receipts', 'tithe_management']);
};

export const requireEventAccess = (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, ['events', 'event_management']);
};

export const requireAttendanceAccess = (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, ['attendance', 'attendance_management']);
};

export const requirePostAccess = (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, ['posts', 'post_management']);
};

export const requireJoomlaAccess = (WrappedComponent) => {
  return withSecureAuth(WrappedComponent, ['joomla', 'joomla_access']);
};

export default withSecureAuth;
